import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import type { AnyMessage } from './chatTypes';
import { isNoticeMessage, isTextMessage, UserState } from './chatTypes';
import { VBox } from '../layout';
import { useTrackElementResize } from '../../lib';
import ChatMessageComponent from './ChatMessageComponents/ChatMessageComponent';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';
import type { ISendEventResponse } from 'matrix-js-sdk';

export interface ChatWindowProps {
  messages: AnyMessage[];
  chatInputComponent: React.JSX.Element;
  chatInputRef?: React.RefObject<HTMLElement | null>;
  emptyStatePanel?: React.JSX.Element;
  header?: React.ReactNode;
  className?: string;
  /**
   * Called when the user scrolls to the top of the message list, to request the
   * next page of older messages. When omitted, scrollback is disabled.
   */
  onLoadOlderMessages?: () => void;
  /** True while an older-messages page is being loaded. */
  loadingOlder?: boolean;
  /** False once the start of the conversation has been reached. */
  hasMoreMessages?: boolean;
  /**
   * Optional node rendered at the top of the list while older messages load.
   * Supplied by the consumer so any user-visible text stays localized.
   */
  loadingOlderIndicator?: React.ReactNode;
  emojiData?: EmojiData;
  reactionClick: (
    eventId: string,
    reactionKey: string,
    roomId?: string | undefined
  ) => Promise<ISendEventResponse | undefined>;
  roomId?: string;
  userStates?: Map<string, UserState>;
  reactionsDisabled?: boolean;
}

function getMessageKey(message: AnyMessage): string {
  return (
    message.id ??
    message.txnId ??
    `${message.date.getTime()}-${message.user.uid}`
  );
}

export function ChatWindow({
  messages,
  chatInputComponent,
  chatInputRef,
  emptyStatePanel,
  header,
  className = '',
  onLoadOlderMessages,
  loadingOlder = false,
  hasMoreMessages = false,
  loadingOlderIndicator,
  emojiData,
  reactionClick,
  roomId,
  userStates,
  reactionsDisabled,
  ...props
}: Readonly<ChatWindowProps>): React.JSX.Element {
  const bodyRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeMessageIndex, setActiveMessageIndex] = useState<number>(() =>
    messages.length > 0 ? messages.length - 1 : -1
  );
  // Text of the latest incoming message, announced via a dedicated polite live
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  // True while the viewport is at (or near) the bottom of the message list.
  const isNearBottomRef = useRef(true);
  // Scroll height captured right before an older-messages load, so the viewport
  // can be re-anchored once the older page is prepended.
  const anchorScrollHeightRef = useRef<number | null>(null);
  const prevLastKeyRef = useRef<string | undefined>(undefined);

  // Scrolls to the very bottom on the next frame, after the browser has laid out
  // freshly added messages. Scrolling synchronously from a layout effect reads a
  // stale scrollHeight and stops short of the bottom.
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    requestAnimationFrame(() => {
      const body = bodyRef.current;
      if (body) {
        body.scrollTo({ top: body.scrollHeight, behavior });
      }
    });
  }, []);

  const [lastMsg, setLastMsg] = useState<AnyMessage | null>(null);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    const lastMessage = messages[messages.length - 1];
    const lastKey = lastMessage ? getMessageKey(lastMessage) : undefined;

    // Drop stale refs when the list shrinks (e.g. switching rooms).
    messageRefs.current.length = messages.length;

    if (anchorScrollHeightRef.current != null && body) {
      // Older messages were prepended: keep the viewport anchored.
      body.scrollTop = body.scrollHeight - anchorScrollHeightRef.current;
      anchorScrollHeightRef.current = null;
    } else {
      const isInitialLoad = prevLastKeyRef.current === undefined;
      const lastKeyChanged = lastKey !== prevLastKeyRef.current;
      const sentByCurrentUser = lastMessage?.user.isSender ?? false;

      if (
        lastKeyChanged &&
        !isInitialLoad &&
        !sentByCurrentUser &&
        lastMessage
      ) {
        setLiveAnnouncement(
          isTextMessage(lastMessage) || isNoticeMessage(lastMessage)
            ? lastMessage.content.text
            : ''
        );
      }
      // Jump to the newest message on first load and when the user sends one;
      // follow incoming messages only while already near the bottom. Pinning
      // also runs when the newest key is unchanged (e.g. the initial page
      // re-map that prepends history) so the viewport does not drift upwards.
      if (
        isInitialLoad ||
        ((sentByCurrentUser || isNearBottomRef.current) &&
          lastMessage &&
          lastMessage.content.text != lastMsg?.content.text) //check if last message changed (don't scroll on reactions)
      ) {
        scrollToBottom(
          isInitialLoad || sentByCurrentUser || !lastKeyChanged
            ? 'auto'
            : 'smooth'
        );
      }
      setLastMsg(lastMessage);
    }

    prevLastKeyRef.current = lastKey;
  }, [lastMsg, messages, scrollToBottom]);

  // The scrollable body often gets its final, constrained height a tick after
  // the messages arrive (flex/height:100% resolving, mask overlays collapsing).
  // The [messages] effect above has already run by then, so re-pin to the bottom
  // when the container resizes - but only while the user is following the latest
  // messages, never when they have scrolled up to read history.
  useTrackElementResize(
    bodyRef,
    true,
    () => {
      if (isNearBottomRef.current) {
        scrollToBottom('auto');
      }
    },
    0
  );

  const handleScroll = () => {
    const body = bodyRef.current;
    if (!body) {
      return;
    }
    // Keep the near-bottom flag current so appended messages know whether the
    // user is following the latest messages or reading older history.
    isNearBottomRef.current =
      body.scrollHeight - (body.scrollTop + body.clientHeight) <= 20;

    if (
      !onLoadOlderMessages ||
      !hasMoreMessages ||
      loadingOlder ||
      body.scrollTop > 20
    ) {
      return;
    }
    // Capture position before the list grows so it can be restored afterwards.
    anchorScrollHeightRef.current = body.scrollHeight;
    onLoadOlderMessages();
  };

  useEffect(() => {
    if (messages.length === 0) {
      setActiveMessageIndex(-1);
      return;
    }

    if (activeMessageIndex < 0 || activeMessageIndex >= messages.length) {
      setActiveMessageIndex(messages.length - 1);
    }
  }, [activeMessageIndex, messages.length]);

  const focusMessage = (index: number) => {
    if (index < 0 || index >= messages.length) {
      return;
    }

    setActiveMessageIndex(index);
    messageRefs.current[index]?.focus();
  };

  // Once the focus leaves the message list the roving tabindex is reset to the
  // newest message, so SHIFT+TAB from the input always lands on the most recent
  // message again.
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget && event.currentTarget.contains(nextTarget)) {
      return;
    }
    if (messages.length > 0) {
      setActiveMessageIndex(messages.length - 1);
    }
  };

  return (
    <VBox {...props} className={`ehs-chat-window ${className}`.trim()}>
      <div className="sr-only" aria-live="polite" role="status">
        {liveAnnouncement}
      </div>
      <VBox
        ref={bodyRef}
        onScroll={handleScroll}
        onBlur={handleBlur}
        className={
          messages.length === 0
            ? 'no-messages chat-window-body'
            : 'chat-window-body'
        }
      >
        {loadingOlder && loadingOlderIndicator}
        {messages.length > 0 && header && (
          <div className="chat-window-header">{header}</div>
        )}
        {messages.length
          ? messages.map((message, index) => (
              <ChatMessageComponent
                key={getMessageKey(message)}
                message={message}
                tabIndex={index === activeMessageIndex ? 0 : -1}
                messageRef={element => {
                  messageRefs.current[index] = element;
                }}
                onFocus={() => setActiveMessageIndex(index)}
                onKeyDown={event => {
                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    focusMessage(index - 1);
                  }

                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    focusMessage(index + 1);
                  }
                }}
                emojiData={emojiData}
                reactionClick={reactionClick}
                roomId={roomId}
                reactionsDisabled={reactionsDisabled}
                userState={
                  userStates?.get(message?.user?.uid) ?? UserState.Offline
                }
              />
            ))
          : emptyStatePanel}
      </VBox>
      <div className="chat-window-input">{chatInputComponent}</div>
    </VBox>
  );
}
